import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Page, Progress, NavButtons, OptionList, GradientSlider, BigTextInput,
  BigTextArea, YesNo, ConfirmScreen, StepTitle, slug,
} from "@/components/wizard";
import { DisclaimerBlock } from "@/components/wizard";
import { postContactRequest } from "@/lib/api";
import { NPA_CONFIG, NPX_CONFIG, GRADIENTS, CONTACT_DAYS, CONTACT_SLOTS, OPTION_ICONS, OPTION_DESCRIPTIONS, DISCLAIMERS } from "@/data/content";
import { postFeedback, postDoctorInvite } from "@/lib/api";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^\+?[0-9][0-9\s\-./()]{5,19}$/;

const signed = (v) => (v > 0 ? `+${v}` : `${v}`);

const parseTime = (t) => {
  const m = /^([01]?\d|2[0-3]):([0-5]\d)$/.exec((t || "").trim());
  return m ? parseInt(m[1], 10) * 60 + parseInt(m[2], 10) : null;
};

const addHours = (t, h) => {
  const base = parseTime(t);
  if (base == null) return null;
  const total = (base + h * 60) % 1440;
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
};

export default function Feedback() {
  const [phase, setPhase] = useState("code");
  const [boxes, setBoxes] = useState(Array(9).fill(""));
  const boxRefs = useRef([]);
  const [code, setCode] = useState("");
  const [path, setPath] = useState(null);
  const [ans, setAns] = useState({});
  const [idx, setIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [hp, setHp] = useState("");
  const [idem] = useState(() => crypto.randomUUID());
  const [docForm, setDocForm] = useState({ code: "", date: "", time: "", mode: "", email: "", notes: "", phone: "" });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [phase, idx]);
  const navigate = useNavigate();

  const steps = useMemo(() => {
    if (!path) return [];
    const cfg = path === "npa" ? NPA_CONFIG : NPX_CONFIG;
    const s = [];
    s.push({ id: "farmaco", kind: "text", title: "Quale farmaco stai assumendo?", placeholder: "Scrivi il nome del farmaco..." });
    s.push({ id: "dosaggio", kind: "text", title: (ans.farmaco || "").trim() || "Farmaco", sub: "Dosaggio?", placeholder: "es. 50 mg" });
    s.push({ id: "effects", kind: "multi", title: "Hai notato effetti collaterali?", sub: "Seleziona uno o più effetti che hai notato durante l'assunzione farmacoterapia", options: [...cfg.effects, "Nessuno"], exclusive: "Nessuno", icons: OPTION_ICONS, descriptions: OPTION_DESCRIPTIONS });
    const selEff = (ans.effects || []).filter((e) => e !== "Nessuno");
    selEff.forEach((e) => s.push({ id: `eff:${e}`, kind: "slider10", title: e, desc: OPTION_DESCRIPTIONS[e], sub: "Quanto è intenso questo effetto?", gradient: GRADIENTS.effects, left: "Per nulla intenso", right: "Estremamente intenso" }));
    s.push({ id: "effects_notes", kind: "textarea", title: "Vuoi aggiungere qualcos'altro?", placeholder: "Scrivi qui eventuali altri effetti o osservazioni..." });
    s.push({ id: "benefits", kind: "multi", title: "Quali benefici hai notato?", sub: "Seleziona uno o più miglioramenti che hai notato durante l'assunzione della farmacoterapia", options: [...cfg.benefits.map((b) => b.label), "Nessuno"], exclusive: "Nessuno", icons: OPTION_ICONS, descriptions: OPTION_DESCRIPTIONS });
    const selBen = (ans.benefits || []).filter((b) => b !== "Nessuno");
    selBen.forEach((b) => {
      const conf = cfg.benefits.find((x) => x.label === b);
      s.push({ id: `ben:${b}`, kind: "slider10", title: b, desc: OPTION_DESCRIPTIONS[b], sub: conf.sub ? `${conf.question} ${conf.sub}` : conf.question, gradient: GRADIENTS.benefits, left: "Nessun miglioramento", right: "Miglioramento massimo" });
    });
    s.push({ id: "benefits_notes", kind: "textarea", title: "Vuoi aggiungere qualcos'altro?", placeholder: "Scrivi qui eventuali altri benefici o osservazioni..." });
    s.push({ id: "sleep", kind: "slider5", title: "Come è cambiato il tuo sonno?", sub: "Rispetto a prima dell'assunzione della farmacoterapia, come valuti complessivamente il tuo sonno?", gradient: GRADIENTS.neutral, left: "Molto peggiorato", center: "Invariato", right: "Molto migliorato" });
    s.push({ id: "appetite", kind: "slider5", title: "Come è cambiato il tuo appetito?", sub: "Rispetto a prima dell'assunzione della farmacoterapia, come valuti complessivamente il tuo appetito?", gradient: GRADIENTS.appetite, left: "Molto diminuito", center: "Invariato", right: "Molto aumentato" });
    if (path === "npx") s.push({ id: "overall", kind: "slider5", title: "Nel complesso, come ti senti rispetto a prima della terapia?", gradient: GRADIENTS.neutral, left: "Molto peggio", center: "Come prima", right: "Molto meglio" });
    s.push({ id: "ora_assunzione", kind: "time", title: "A che ora assumi il farmaco?" });
    if (path === "npa") {
      s.push({ id: "effetto_inizio", kind: "hours", title: "Dopo quante ore senti l'effetto del farmaco?" });
      s.push({ id: "effetto_fine", kind: "hours", title: "Dopo quante ore non senti più l'effetto del farmaco?" });
    }
    s.push({ id: "final_notes", kind: "textarea", title: "Vuoi aggiungere qualcos'altro?", sub: "Se vuoi, scrivi qualcosa che non è emerso nelle domande precedenti", placeholder: "Scrivi qui..." });
    s.push({ id: "summary", kind: "summary" });
    s.push({ id: "disclaimer", kind: "disclaimer", title: DISCLAIMERS.feedback.title });
    s.push({ id: "contact", kind: "yesno", title: "Necessiti di un contatto telefonico?" });
    if (ans.contact === "si") {
      s.push({ id: "phone", kind: "phone", title: "Inserisci il tuo numero di telefono", sub: "Sono accettati prefissi internazionali (es. +41, +39)." });
      s.push({ id: "days", kind: "multi", title: "Quale giorno preferisci per il contatto telefonico?", options: CONTACT_DAYS });
      s.push({ id: "slots", kind: "multi", title: "In quale fascia oraria preferisci il contatto telefonico?", options: CONTACT_SLOTS });
    }
    if (ans.send_feedback) {
      s.push({ id: "reminder", kind: "email", title: "Vuoi ricevere un promemoria?", sub: "Se vuoi, inserisci la tua email e ti ricorderemo tra 7 giorni di compilare un nuovo feedback sulla terapia.", placeholder: "nome@email.com" });
    }
    return s;
  }, [path, ans]);

  const step = steps[Math.min(idx, steps.length - 1)];
  const set = (id) => (v) => setAns((a) => ({ ...a, [id]: v }));

  const canContinue = () => {
    if (!step) return false;
    switch (step.kind) {
      case "disclaimer": return true;
      case "multi": return (ans[step.id] || []).length > 0;
      case "text": return (ans[step.id] || "").trim().length >= 1;
      case "time": return parseTime(ans[step.id]) !== null;
      case "hours": return ans[step.id] !== undefined && ans[step.id] !== null;
      case "slider10": return ans[step.id] !== undefined && ans[step.id] !== null;
      case "slider5": return true;
      case "yesno": return !!ans[step.id];
      case "phone": return PHONE_RE.test((ans.phone || "").trim());
      case "email": { const v = (ans.reminder || "").trim(); return !v || EMAIL_RE.test(v); }
      default: return true;
    }
  };

  const letters = boxes.slice(0, 3).join("");
  const digits = boxes.slice(3).join("");
  const codeReady = (/^(NPA|NPX)$/.test(letters) && digits.length >= 1) || (letters === "NIC" && digits === "1");

  const onBoxChange = (i, raw) => {
    const ch = raw.slice(-1).toUpperCase();
    if (i < 3 ? !/^[A-Z]?$/.test(ch) : !/^[0-9]?$/.test(ch)) return;
    setBoxes((b) => b.map((x, j) => (j === i ? ch : x)));
    if (ch && i < 8) boxRefs.current[i + 1]?.focus();
  };

  const onBoxKeyDown = (i, e) => {
    if (e.key === "Backspace" && !boxes[i] && i > 0) boxRefs.current[i - 1]?.focus();
  };

  const startWizard = () => {
    if (letters === "NIC" && digits === "1") {
      setPhase("doctor");
      return;
    }
    setCode(`${letters}-${digits}`);
    setPath(letters === "NPA" ? "npa" : "npx");
    setIdx(0);
    setPhase("menu");
  };

  const submit = async () => {
    setSubmitting(true);
    setSubmitError("");
    const selEff = (ans.effects || []).filter((e) => e !== "Nessuno");
    const selBen = (ans.benefits || []).filter((b) => b !== "Nessuno");
    const payload = {
      idempotency_key: idem,
      code,
      path,
      farmaco: (ans.farmaco || "").trim(),
      dosaggio: (ans.dosaggio || "").trim(),
      ora_assunzione: (ans.ora_assunzione || "").trim(),
      effetto_inizio: ans.effetto_inizio ?? null,
      effetto_fine: ans.effetto_fine ?? null,
      effects: selEff.map((e) => ({ label: e, intensity: ans[`eff:${e}`] ?? 0 })),
      effects_notes: ans.effects_notes || "",
      benefits: selBen.map((b) => ({ label: b, intensity: ans[`ben:${b}`] ?? 0 })),
      benefits_notes: ans.benefits_notes || "",
      sleep: ans.sleep ?? 0,
      appetite: ans.appetite ?? 0,
      overall: path === "npx" ? ans.overall ?? 0 : null,
      final_notes: ans.final_notes || "",
      contact_requested: ans.contact === "si",
      phone: ans.phone || "",
      days: ans.days || [],
      slots: ans.slots || [],
      reminder_email: (ans.reminder || "").trim() || null,
      website: hp,
    };
    try {
      if (ans.send_feedback) {
        await postFeedback(payload);
      } else if (ans.contact === "si") {
        await postContactRequest({
          idempotency_key: idem,
          code,
          phone: (ans.phone || "").trim(),
          days: ans.days || [],
          slots: ans.slots || [],
          website: hp,
        });
      }
      setPhase("done");
    } catch (e) {
      setSubmitError("Invio non riuscito. Controlla la connessione e riprova.");
    } finally {
      setSubmitting(false);
    }
  };

  if (phase === "doctor") {
    const setDoc = (k) => (e) => setDocForm((p) => ({ ...p, [k]: e.target.value }));
    const hasDateTime = docForm.date.trim() || docForm.time.trim();
    const apptOk = !hasDateTime || (docForm.date.trim() && docForm.time.trim() && docForm.mode);
    const ready = docForm.code.trim() && docForm.email.trim() && apptOk;
    const phoneDigits = docForm.phone.replace(/[^\d]/g, "").replace(/^00/, "");
    const waReady = docForm.code.trim() && apptOk;
    const sendInvite = async () => {
      setSubmitting(true);
      setSubmitError("");
      try {
        await postDoctorInvite({
          patient_code: docForm.code.trim(),
          date: docForm.date.trim(),
          time: docForm.time.trim(),
          mode: docForm.mode,
          email: docForm.email.trim(),
          notes: docForm.notes.trim(),
        });
        setPhase("doctor-done");
      } catch (e) {
        setSubmitError("Invio non riuscito. Controlla i dati e riprova.");
      } finally {
        setSubmitting(false);
      }
    };
    const sendWhatsApp = () => {
      const modeLabel = { presenza: "in presenza", videochiamata: "in videochiamata", telefonico: "telefonico" }[docForm.mode];
      const lines = [
        "Gentile paziente,",
        "Le comunichiamo il Suo codice personale e anonimo per accedere all'area riservata del sito pipitone.ch:",
        "",
        docForm.code.trim().toUpperCase(),
        "",
        "Accedendo all'area riservata di pipitone.ch potrà utilizzare il codice per inviare un feedback sulla farmacoterapia e richiedere una nuova ricetta.",
      ];
      if (docForm.date.trim() && docForm.time.trim()) {
        lines.push("", `Le ricordiamo inoltre che il Suo prossimo appuntamento è fissato per il ${docForm.date.trim()} alle ore ${docForm.time.trim()}, ${modeLabel}.`);
      }
      if (docForm.notes.trim()) {
        lines.push("", `Note: ${docForm.notes.trim()}`);
      }
      lines.push("", "Cordiali saluti,", "Dr. med. Nicolò Pipitone", "Psichiatra e Psicoterapeuta FMH");
      const waBase = phoneDigits.length >= 8 ? `https://wa.me/${phoneDigits}` : "https://wa.me/";
      window.open(`${waBase}?text=${encodeURIComponent(lines.join("\n"))}`, "_blank");
    };
    const inputCls =
      "w-full min-h-[52px] px-4 py-3 rounded-xl border border-[#E5E7EB] bg-white text-base text-[#142D43] focus:border-[#142D43] focus:shadow-[0_0_0_3px_rgba(20,45,67,0.08)] outline-none transition-[border-color,box-shadow]";
    const modeBtn = (v, label) => (
      <button
        type="button"
        key={v}
        data-testid={`doctor-mode-${v}`}
        onClick={() => setDocForm((p) => ({ ...p, mode: v }))}
        className={`flex-1 min-h-[52px] px-5 rounded-[14px] text-[15px] transition-[border-color,background-color,box-shadow,transform] duration-[180ms] ${
          docForm.mode === v
            ? "bg-[#F4F6F8] border-2 border-[#142D43] text-[#142D43] font-semibold shadow-[0_3px_10px_rgba(20,45,67,0.08)]"
            : "bg-white border border-[#E5E7EB] text-[#142D43] hover:border-[rgba(20,45,67,0.25)] hover:bg-[#F4F6F8] shadow-[0_3px_10px_rgba(20,45,67,0.05)]"
        }`}
      >
        {label}
      </button>
    );
    return (
      <Page narrow>
        <div data-testid="doctor-dashboard">
          <h1 className="mt-8 text-center text-xl sm:text-2xl font-semibold tracking-wide text-[#142D43] uppercase">
            Area medico
          </h1>
          <p className="mt-3 text-sm sm:text-base text-[#374151] leading-relaxed text-center">
            Prepara l'email con il codice personale e il prossimo appuntamento.
          </p>
          <div className="mt-6 flex flex-col gap-3">
            <input
              value={docForm.code}
              onChange={setDoc("code")}
              placeholder="Codice personale (es. NPA-1)"
              data-testid="doctor-code"
              className={inputCls}
            />
            <input
              value={docForm.date}
              onChange={setDoc("date")}
              placeholder="Data (es. 25 settembre 2026)"
              data-testid="doctor-date"
              className={inputCls}
            />
            <input
              value={docForm.time}
              onChange={setDoc("time")}
              placeholder="Orario (es. 15:00)"
              data-testid="doctor-time"
              className={inputCls}
            />
            <div className="flex gap-3" data-testid="doctor-mode">
              {modeBtn("presenza", "In presenza")}
              {modeBtn("videochiamata", "Videochiamata")}
              {modeBtn("telefonico", "Telefonico")}
            </div>
            <input
              type="email"
              value={docForm.email}
              onChange={setDoc("email")}
              placeholder="Email"
              data-testid="doctor-email"
              className={inputCls}
            />
            <input
              type="tel"
              value={docForm.phone}
              onChange={setDoc("phone")}
              placeholder="Telefono WhatsApp (es. +41 79 123 45 67)"
              data-testid="doctor-phone"
              className={inputCls}
            />
            <textarea
              value={docForm.notes}
              onChange={setDoc("notes")}
              placeholder="Note (opzionali)"
              data-testid="doctor-notes"
              rows={3}
              className={`${inputCls} resize-none`}
            />
          </div>
          {submitError ? (
            <p className="mt-4 text-sm text-red-700" data-testid="submit-error">
              {submitError}
            </p>
          ) : null}
          <div className="mt-6 flex gap-3">
            <button
              type="button"
              onClick={sendInvite}
              disabled={!ready || submitting}
              data-testid="doctor-send-button"
              className="flex-1 bg-[#142D43] text-white border border-[rgba(255,255,255,0.08)] shadow-[0_2px_0_#0F2233,0_6px_14px_rgba(20,45,67,0.16),inset_0_1px_0_rgba(255,255,255,0.12)] hover:bg-[#1E3F5C] hover:shadow-[0_4px_0_#0F2233,0_10px_22px_rgba(20,45,67,0.20),inset_0_1px_0_rgba(255,255,255,0.15)] hover:-translate-y-[2px] active:shadow-[0_1px_0_#0F2233,0_3px_7px_rgba(20,45,67,0.14),inset_0_1px_2px_rgba(0,0,0,0.08)] active:translate-y-[2px] font-semibold tracking-[0.01em] px-4 rounded-[14px] transition-[background-color,transform,box-shadow] duration-[180ms] active:duration-100 text-[15px] h-[54px] flex items-center justify-center cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed disabled:transform-none"
            >
              {submitting ? "INVIO…" : "INVIA EMAIL"}
            </button>
            <button
              type="button"
              onClick={sendWhatsApp}
              disabled={!waReady}
              data-testid="doctor-whatsapp-button"
              className="flex-1 bg-[#142D43] text-white border border-[rgba(255,255,255,0.08)] shadow-[0_2px_0_#0F2233,0_6px_14px_rgba(20,45,67,0.16),inset_0_1px_0_rgba(255,255,255,0.12)] hover:bg-[#1E3F5C] hover:shadow-[0_4px_0_#0F2233,0_10px_22px_rgba(20,45,67,0.20),inset_0_1px_0_rgba(255,255,255,0.15)] hover:-translate-y-[2px] active:shadow-[0_1px_0_#0F2233,0_3px_7px_rgba(20,45,67,0.14),inset_0_1px_2px_rgba(0,0,0,0.08)] active:translate-y-[2px] font-semibold tracking-[0.01em] px-4 rounded-[14px] transition-[background-color,transform,box-shadow] duration-[180ms] active:duration-100 text-[15px] h-[54px] flex items-center justify-center cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed disabled:transform-none"
            >
              WHATSAPP
            </button>
          </div>
        </div>
      </Page>
    );
  }

  if (phase === "doctor-done") {
    return (
      <ConfirmScreen
        title="Email inviata"
        text="L'email con il codice personale e il prossimo appuntamento è stata inviata correttamente."
      />
    );
  }

  if (phase === "done") {
    if (ans.send_feedback) {
      return (
        <ConfirmScreen
          title="Feedback inviato"
          text="Grazie. Il tuo feedback è stato inviato correttamente."
          note="Le informazioni inviate non sostituiscono una valutazione medica. In caso di sintomi importanti, improvvisi o urgenti, non attendere una risposta tramite questa web app e rivolgiti ai servizi sanitari appropriati."
        />
      );
    }
    return (
      <ConfirmScreen
        title="Feedback non inviato"
        text={ans.contact === "si" ? "La richiesta di contatto telefonico è stata inviata allo studio." : "Hai scelto di non inviare il feedback."}
        note="In caso di sintomi importanti, improvvisi o urgenti, rivolgiti ai servizi sanitari appropriati."
      />
    );
  }

  if (phase === "code") {
    return (
      <Page narrow>
        <div data-testid="code-entry-screen">
          <h1 className="mt-8 text-center text-xl sm:text-2xl font-semibold tracking-wide text-[#142D43] uppercase">
            Codice personale
          </h1>
          <p className="mt-3 text-center text-sm sm:text-base font-medium tracking-wide text-[#142D43] uppercase" data-testid="code-subtitle">
            Feedback farmacoterapia & nuova ricetta
          </p>
          <p className="mt-2 text-center text-xs text-[#374151] leading-relaxed" data-testid="code-hint">
            Già paziente del Dr. med. Pipitone? Richieda il suo codice
          </p>
          <div className="mt-8 flex items-center justify-center gap-1.5" data-testid="code-boxes">
            {boxes.map((v, i) => (
              <span key={i} className="flex items-center gap-1.5">
                {i === 3 ? <span className="text-xl text-[#374151] select-none px-0.5">–</span> : null}
                <input
                  ref={(el) => (boxRefs.current[i] = el)}
                  value={v}
                  onChange={(e) => onBoxChange(i, e.target.value)}
                  onKeyDown={(e) => onBoxKeyDown(i, e)}
                  maxLength={1}
                  inputMode={i < 3 ? "text" : "numeric"}
                  autoFocus={i === 0}
                  data-testid={`code-box-${i}`}
                  aria-label={i < 3 ? `Lettera ${i + 1}` : `Cifra ${i - 2}`}
                  className="w-8 h-12 sm:w-10 sm:h-14 text-center text-lg sm:text-xl font-mono uppercase border border-[#E5E7EB] rounded-[12px] bg-white text-[#142D43] focus:border-[#142D43] focus:shadow-[0_0_0_3px_rgba(20,45,67,0.08)] outline-none transition-[border-color,box-shadow] caret-color-[#142D43]"
                />
              </span>
            ))}
          </div>
          <input
            type="text"
            value={hp}
            onChange={(e) => setHp(e.target.value)}
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            className="absolute left-[-9999px] opacity-0 h-0 w-0"
            data-testid="hp-field"
          />
          <button
            type="button"
            onClick={startWizard}
            disabled={!codeReady}
            data-testid="code-submit-button"
            className="mt-10 w-full bg-[#142D43] text-white border border-[rgba(255,255,255,0.08)] shadow-[0_2px_0_#0F2233,0_6px_14px_rgba(20,45,67,0.16),inset_0_1px_0_rgba(255,255,255,0.12)] hover:bg-[#1E3F5C] hover:shadow-[0_4px_0_#0F2233,0_10px_22px_rgba(20,45,67,0.20),inset_0_1px_0_rgba(255,255,255,0.15)] hover:-translate-y-[2px] active:shadow-[0_1px_0_#0F2233,0_3px_7px_rgba(20,45,67,0.14),inset_0_1px_2px_rgba(0,0,0,0.08)] active:translate-y-[2px] font-semibold tracking-[0.01em] px-7 rounded-[14px] transition-[background-color,transform,box-shadow] duration-[180ms] active:duration-100 text-[15px] h-[54px] flex items-center justify-center cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed disabled:transform-none"
          >
            CONTINUA
          </button>
        </div>
      </Page>
    );
  }

  if (phase === "menu") {
    return (
      <Page narrow>
        <div data-testid="reserved-menu-screen">
          <h1 className="mt-8 text-center text-xl sm:text-2xl font-semibold tracking-wide text-[#142D43] uppercase">
            Area riservata
          </h1>
          <div className="mt-10 flex flex-col gap-4">
            <button
              type="button"
              data-testid="menu-feedback-button"
              onClick={() => { setIdx(0); setPhase("wizard"); }}
              className="w-full bg-white text-[#142D43] border border-[#E5E7EB] shadow-[0_3px_10px_rgba(20,45,67,0.06)] hover:bg-[#F4F6F8] hover:border-[rgba(20,45,67,0.25)] hover:-translate-y-[2px] active:translate-y-[1px] active:shadow-[0_1px_4px_rgba(20,45,67,0.06)] font-semibold tracking-[0.01em] py-4 px-6 rounded-[14px] transition-[background-color,border-color,transform,box-shadow] duration-[180ms] text-[15px] min-h-[56px]"
            >
              FEEDBACK FARMACOTERAPIA
            </button>
            <button
              type="button"
              data-testid="menu-prescription-button"
              onClick={() => navigate("/ricetta", { state: { code } })}
              className="w-full bg-white text-[#142D43] border border-[#E5E7EB] shadow-[0_3px_10px_rgba(20,45,67,0.06)] hover:bg-[#F4F6F8] hover:border-[rgba(20,45,67,0.25)] hover:-translate-y-[2px] active:translate-y-[1px] active:shadow-[0_1px_4px_rgba(20,45,67,0.06)] font-semibold tracking-[0.01em] py-4 px-6 rounded-[14px] transition-[background-color,border-color,transform,box-shadow] duration-[180ms] text-[15px] min-h-[56px]"
            >
              RICHIEDI UNA RICETTA
            </button>
          </div>
          <p className="mt-6 text-xs sm:text-sm text-[#374151] leading-relaxed text-center" data-testid="menu-privacy-note">
            Nota bene: questo sito non conserva mai nessun dato.
          </p>
        </div>
      </Page>
    );
  }

  const summaryRows = () => {
    const rows = [];
    rows.push({
      label: "Farmaco",
      value: `${(ans.farmaco || "").trim()}${(ans.dosaggio || "").trim() ? ` — ${(ans.dosaggio || "").trim()}` : ""}`,
      testId: "summary-farmaco",
    });
    const selEff = (ans.effects || []).filter((e) => e !== "Nessuno");
    rows.push({
      label: "Effetti collaterali",
      value: selEff.length ? selEff.map((e) => `${e} (${ans[`eff:${e}`] ?? 0}/10)`).join(", ") : "Nessuno",
      testId: "summary-effects",
    });
    if ((ans.effects_notes || "").trim()) rows.push({ label: "Note sugli effetti", value: ans.effects_notes, testId: "summary-effects-notes" });
    const selBen = (ans.benefits || []).filter((b) => b !== "Nessuno");
    rows.push({
      label: "Benefici",
      value: selBen.length ? selBen.map((b) => `${b} (${ans[`ben:${b}`] ?? 0}/10)`).join(", ") : "Nessuno",
      testId: "summary-benefits",
    });
    if ((ans.benefits_notes || "").trim()) rows.push({ label: "Note sui benefici", value: ans.benefits_notes, testId: "summary-benefits-notes" });
    rows.push({ label: "Sonno", value: signed(ans.sleep ?? 0), testId: "summary-sleep" });
    rows.push({ label: "Appetito", value: signed(ans.appetite ?? 0), testId: "summary-appetite" });
    if (path === "npx") rows.push({ label: "Valutazione complessiva", value: signed(ans.overall ?? 0), testId: "summary-overall" });
    rows.push({ label: "Orario di assunzione", value: ans.ora_assunzione || "—", testId: "summary-orario" });
    if (path === "npa") {
      rows.push({
        label: "Inizio effetto",
        value: ans.effetto_inizio != null ? `${addHours(ans.ora_assunzione, ans.effetto_inizio)} (+${ans.effetto_inizio} h)` : "—",
        testId: "summary-effetto-inizio",
      });
      rows.push({
        label: "Fine effetto",
        value: ans.effetto_fine != null ? `${addHours(ans.ora_assunzione, ans.effetto_fine)} (+${ans.effetto_fine} h)` : "—",
        testId: "summary-effetto-fine",
      });
    }
    if ((ans.final_notes || "").trim()) rows.push({ label: "Osservazioni", value: ans.final_notes, testId: "summary-notes" });
    rows.push({
      label: "Contatto telefonico",
      value: ans.contact === "si"
        ? `Sì — ${ans.phone}, ${(ans.days || []).join(", ")}, ${(ans.slots || []).join(", ")}`
        : "No",
      testId: "summary-contact",
    });
    rows.push({ label: "Promemoria", value: (ans.reminder || "").trim() ? `Sì — ${ans.reminder}` : "No", testId: "summary-reminder" });
    return rows;
  };

  return (
    <Page narrow>
      <Progress current={idx + 1} total={steps.length} />
      <div data-testid={`feedback-step-${slug(step.id)}`}>
        <StepTitle title={step.title} sub={step.sub} desc={step.desc} />
        {step.kind === "disclaimer" ? (
          <>
            <DisclaimerBlock content={DISCLAIMERS.feedback} withCheckbox={false} />
            <div className="mt-6 flex flex-col-reverse sm:flex-row gap-3">
              <button
                type="button"
                data-testid="disclaimer-no-button"
                onClick={() => { set("send_feedback")(false); setIdx(idx + 1); }}
                className="flex-1 px-6 h-[54px] rounded-[14px] text-[15px] font-semibold text-[#142D43] bg-white border border-[#D1D5DB] shadow-[0_2px_0_#D1D5DB,0_4px_10px_rgba(20,45,67,0.08)] hover:border-[#142D43] active:translate-y-[1px] transition-[border-color,transform,box-shadow] duration-[180ms] cursor-pointer"
              >
                NO, NON INVIARE
              </button>
              <button
                type="button"
                data-testid="disclaimer-yes-button"
                onClick={() => { set("send_feedback")(true); setIdx(idx + 1); }}
                className="flex-1 px-6 h-[54px] rounded-[14px] text-[15px] bg-[#142D43] text-white border border-[rgba(255,255,255,0.08)] shadow-[0_2px_0_#0F2233,0_6px_14px_rgba(20,45,67,0.16),inset_0_1px_0_rgba(255,255,255,0.12)] hover:bg-[#1E3F5C] hover:-translate-y-[2px] active:translate-y-[2px] font-semibold tracking-[0.01em] transition-[background-color,transform,box-shadow] duration-[180ms] cursor-pointer"
              >
                SÌ, INVIA
              </button>
            </div>
          </>
        ) : null}

        {step.kind === "multi" && (
          <OptionList options={step.options} values={ans[step.id] || []} onChange={set(step.id)} exclusive={step.exclusive || null} icons={step.icons || null} descriptions={step.descriptions || null} />
        )}

        {step.kind === "slider10" && (
          <>
            <GradientSlider
              min={0}
              max={10}
              value={ans[step.id] ?? 5}
              displayValue={ans[step.id] == null ? "—" : `${ans[step.id]}/10`}
              onChange={set(step.id)}
              gradient={step.gradient}
              leftLabel={step.left}
              rightLabel={step.right}
              testId={`slider-${slug(step.id)}`}
            />
            {ans[step.id] == null ? (
              <p className="mt-4 text-center text-xs text-[#374151]">Tocca o trascina il cursore per selezionare un valore.</p>
            ) : null}
          </>
        )}

        {step.kind === "slider5" && (
          <GradientSlider
            min={-5}
            max={5}
            value={ans[step.id] ?? 0}
            displayValue={signed(ans[step.id] ?? 0)}
            onChange={set(step.id)}
            gradient={step.gradient}
            leftLabel={step.left}
            centerLabel={step.center}
            rightLabel={step.right}
            testId={`slider-${slug(step.id)}`}
          />
        )}

        {step.kind === "textarea" && (
          <BigTextArea value={ans[step.id] || ""} onChange={set(step.id)} placeholder={step.placeholder} testId={`textarea-${slug(step.id)}`} />
        )}

        {step.kind === "text" && (
          <BigTextInput value={ans[step.id] || ""} onChange={set(step.id)} placeholder={step.placeholder} testId={`input-${slug(step.id)}`} autoFocus />
        )}

        {step.kind === "time" && (
          <BigTextInput value={ans[step.id] || ""} onChange={set(step.id)} placeholder="08:00" testId="input-ora-assunzione" type="time" />
        )}

        {step.kind === "hours" && (
          <>
            <GradientSlider
              min={0}
              max={12}
              step={0.5}
              value={ans[step.id] ?? 0}
              displayValue={ans[step.id] == null ? "—" : `${addHours(ans.ora_assunzione, ans[step.id])} (+${String(ans[step.id]).replace(".", ",")} h)`}
              onChange={set(step.id)}
              gradient={GRADIENTS.effects}
              leftLabel={ans.ora_assunzione || "Orario di assunzione"}
              rightLabel="+12 h"
              testId={`slider-${slug(step.id)}`}
            />
            {ans[step.id] == null ? (
              <p className="mt-4 text-center text-xs text-[#374151]">Tocca o trascina il cursore per indicare le ore.</p>
            ) : null}
          </>
        )}

        {step.kind === "yesno" && <YesNo value={ans[step.id] || ""} onChange={set(step.id)} />}

        {step.kind === "phone" && (
          <BigTextInput value={ans.phone || ""} onChange={set("phone")} placeholder="+41 79 000 00 00" testId="phone-input" type="tel" />
        )}

        {step.kind === "email" && (
          <BigTextInput value={ans.reminder || ""} onChange={set("reminder")} placeholder="nome@email.com" testId="reminder-email-input" type="email" />
        )}

        {step.kind === "summary" && (
          <div className="mt-8 border border-[#E5E7EB] rounded-xl divide-y divide-[#E5E7EB]" data-testid="feedback-summary">
            {summaryRows().map((r) => (
              <div key={r.testId} className="px-5 py-4" data-testid={r.testId}>
                <p className="text-xs text-[#374151] uppercase tracking-wide">{r.label}</p>
                <p className="mt-1 text-sm sm:text-base text-[#142D43] leading-relaxed">{r.value}</p>
              </div>
            ))}
          </div>
        )}

        {submitError ? <p className="mt-4 text-sm text-red-700" data-testid="submit-error">{submitError}</p> : null}

        {step.kind !== "disclaimer" ? (
          <NavButtons
            onBack={() => (idx === 0 ? setPhase("menu") : setIdx(idx - 1))}
            onNext={() => (idx === steps.length - 1 ? submit() : setIdx(idx + 1))}
            canNext={canContinue()}
            nextLabel={ans.send_feedback !== false && (step.kind === "summary" || idx === steps.length - 1) ? "INVIA FEEDBACK" : "CONTINUA"}
            nextTestId={ans.send_feedback !== false && (step.kind === "summary" || idx === steps.length - 1) ? "feedback-submit-button" : "wizard-next-button"}
            loading={submitting}
          />
        ) : null}
      </div>
    </Page>
  );
}
